import { useRef } from 'react'
import { Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import classes from './FocusPointPicker.module.css'

/**
 * Pick the part of a photograph that has to survive the crop.
 *
 * The whole source image is shown, not the cropped rendition: the point being
 * chosen is a point of the original, and choosing it against an already-cut
 * picture would hide exactly the part the operator is trying to bring back.
 *
 * The image is sized with max-width/max-height inside a centred box rather than
 * object-fit, so its own bounding box is the drawn area — a click maps to a
 * percentage of the picture with no letterbox arithmetic.
 *
 * `readOnly` shows the same whole picture and point without taking clicks (the slide image
 * panel while Focus is closed).
 */
export function FocusPointPicker({ src, focalPoint, onPick, readOnly = false }) {
  const { t } = useTranslation()
  const imageRef = useRef(null)

  const pick = (event) => {
    const image = imageRef.current
    if (!image) return
    const bounds = image.getBoundingClientRect()
    if (!bounds.width || !bounds.height) return
    const clamp = (value) => Math.min(100, Math.max(0, value))
    onPick({
      x: clamp((event.clientX - bounds.left) / bounds.width * 100),
      y: clamp((event.clientY - bounds.top) / bounds.height * 100),
    })
  }

  const nudge = (event) => {
    const step = event.shiftKey ? 10 : 1
    const moves = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0],
      ArrowUp: [0, -step], ArrowDown: [0, step],
    }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    const clamp = (value) => Math.min(100, Math.max(0, value))
    onPick({
      x: clamp(Number(focalPoint?.x ?? 50) + move[0]),
      y: clamp(Number(focalPoint?.y ?? 50) + move[1]),
    })
  }

  return (
    <div className={classes.wrap}>
      <div
        className={classes.stage}
        {...(readOnly ? {} : {
          role: 'application',
          'aria-label': t('editorV2.form.fields.focusPoint'),
          tabIndex: 0,
          onKeyDown: nudge,
        })}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <img
          ref={imageRef}
          className={classes.image}
          data-readonly={readOnly ? 'true' : undefined}
          src={src}
          alt=""
          draggable={false}
          onClick={readOnly ? undefined : pick}
        />
        {focalPoint && (
          <span
            className={classes.marker}
            style={{ left: `${focalPoint.x}%`, top: `${focalPoint.y}%` }}
            aria-hidden
          />
        )}
      </div>
      {!readOnly && (
        <Text size="xs" c="dimmed" ta="center">
          {t('editorV2.form.fields.focusHint')}
        </Text>
      )}
    </div>
  )
}
